import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import LinkTestRunModal from "../../../components/testplan/LinkTestRunModal";
import { testPlanService } from "../../../services/testPlanService";
import { userService } from "../../../services/userService";
import { useAuth } from "../../../context/AuthContext";
import FormSkeleton from "../../../components/skeletons/FormSkeleton";

const emptyFeatureRow = () => ({
  reference: "",
  feature_title: "",
  type: "",
  qa_owner: "",
  start_date: "",
  end_date: "",
});

const emptyEnvRow = () => ({ environment: "", purpose: "" });
const emptyScheduleRow = () => ({ activity: "", date: "" });
const emptyTeamRow = () => ({ user_id: "", role: "", responsibility: "" });

const initialForm = {
  name: "",
  reference: "",
  startDate: "",
  endDate: "",
  notifications: "",
  description: "",
  assignedTo: "",
  scopeIn: [""],
  scopeOut: [""],
  testingTypes: [""],
  executionApproach: [""],
  tools: [""],
  environment: [emptyEnvRow()],
  schedule: [emptyScheduleRow()],
  features: [emptyFeatureRow()],
  entryCriteria: [""],
  exitCriteria: [""],
  team: [emptyTeamRow()],
};

function parseReferenceString(raw) {
  if (!raw || !String(raw).trim()) return [];
  return String(raw)
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function toStrList(rows, key) {
  if (!rows?.length) return [""];
  const mapped = rows
    .map((r) => (key ? (r[key] ?? "") : r))
    .map((s) => String(s ?? "").trim());
  return mapped.length ? mapped : [""];
}

function mapPlanToForm(p) {
  return {
    name: p.name || "",
    reference: Array.isArray(p.reference)
      ? p.reference.join(", ")
      : p.reference || "",
    startDate: p.start_date ? String(p.start_date).slice(0, 10) : "",
    endDate: p.end_date ? String(p.end_date).slice(0, 10) : "",
    notifications: "",
    description: p.description || "",
    assignedTo: p.assigned_to ? String(p.assigned_to) : "",
    scopeIn: toStrList(p.scopeIn, "item_text"),
    scopeOut: toStrList(p.scopeOut, "item_text"),
    testingTypes: toStrList(p.testingTypes, "type_name"),
    executionApproach: toStrList(p.executionApproach, "approach_text"),
    tools: toStrList(p.tools, "tool_name"),
    environment:
      p.environment?.length > 0
        ? p.environment.map((r) => ({
            environment: r.environment ?? "",
            purpose: r.purpose ?? "",
          }))
        : [emptyEnvRow()],
    schedule:
      p.schedule?.length > 0
        ? p.schedule.map((r) => ({
            activity: r.activity ?? "",
            date: r.date ? String(r.date).slice(0, 10) : "",
          }))
        : [emptyScheduleRow()],
    features:
      p.features?.length > 0
        ? p.features.map((r) => ({
            reference: r.reference ?? "",
            feature_title: r.feature_title ?? "",
            type: r.type ?? "",
            qa_owner: r.qa_owner ?? "",
            start_date: r.start_date ? String(r.start_date).slice(0, 10) : "",
            end_date: r.end_date ? String(r.end_date).slice(0, 10) : "",
          }))
        : [emptyFeatureRow()],
    entryCriteria: toStrList(p.entryCriteria, "text"),
    exitCriteria: toStrList(p.exitCriteria, "text"),
    team:
      p.team?.length > 0
        ? p.team.map((r) => ({
            user_id: r.user_id ? String(r.user_id) : "",
            role: r.role ?? "",
            responsibility: r.responsibility ?? "",
          }))
        : [emptyTeamRow()],
  };
}

function buildPayload(form, fallbackUserId) {
  const refArray = parseReferenceString(form.reference);
  const strs = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map((s) => String(s ?? "").trim())
      .filter(Boolean);

  const features = (form.features || [])
    .map((f) => ({
      reference: f.reference?.trim() || null,
      feature_title: f.feature_title?.trim() || null,
      type: f.type?.trim() || null,
      qa_owner: f.qa_owner?.trim() || null,
      start_date: f.start_date || null,
      end_date: f.end_date || null,
    }))
    .filter(
      (f) =>
        f.reference ||
        f.feature_title ||
        f.type ||
        f.qa_owner ||
        f.start_date ||
        f.end_date,
    );

  const team = (form.team || [])
    .map((t) => ({
      user_id: t.user_id || null,
      role: t.role?.trim() || "",
      responsibility: t.responsibility?.trim() || "",
    }))
    .filter((t) => t.user_id || t.role || t.responsibility);

  return {
    name: form.name.trim(),
    description: form.description?.trim() || "",
    assignedTo: form.assignedTo || fallbackUserId,
    reference: refArray,
    start_date: form.startDate || null,
    end_date: form.endDate || null,
    scopeIn: strs(form.scopeIn),
    scopeOut: strs(form.scopeOut),
    testingTypes: strs(form.testingTypes),
    executionApproach: strs(form.executionApproach),
    tools: strs(form.tools),
    environment: (form.environment || [])
      .map((e) => ({
        environment: e.environment?.trim() || "",
        purpose: e.purpose?.trim() || "",
      }))
      .filter((e) => e.environment || e.purpose),
    schedule: (form.schedule || [])
      .map((s) => ({
        activity: s.activity?.trim() || "",
        date: s.date || null,
      }))
      .filter((s) => s.activity),
    entryCriteria: strs(form.entryCriteria),
    exitCriteria: strs(form.exitCriteria),
    team,
    features,
  };
}

/**
 * Create / edit Test Plan — calls testPlanService.create / update (POST testplan/create, testplan/update).
 */
const CreateTestPlan = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const projectId = searchParams.get("project_id");
  const projectName = searchParams.get("project_name") || "Project";
  const clientIdParam = searchParams.get("client_id");
  const cloneId = searchParams.get("clone_id");
  const { user } = useAuth();
  const resolvedClientId = clientIdParam || user?.client_id;

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [loading, setLoading] = useState(!!editId || !!cloneId);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [linkedRunIds, setLinkedRunIds] = useState([]);
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const title = editId ? "Edit Test Plan" : "New Test Plan";

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: null }));
  };

  const updateArrayItem = (key, index, field, value) => {
    setForm((f) => {
      const arr = [...(f[key] || [])];
      if (typeof field === "string") {
        arr[index] = { ...arr[index], [field]: value };
      } else {
        arr[index] = value;
      }
      return { ...f, [key]: arr };
    });
  };

  const addArrayRow = (key, factory) => {
    setForm((f) => ({ ...f, [key]: [...(f[key] || []), factory()] }));
  };

  const removeArrayRow = (key, index) => {
    setForm((f) => {
      const arr = [...(f[key] || [])];
      arr.splice(index, 1);
      return { ...f, [key]: arr.length ? arr : [factoryForKey(key)] };
    });
  };

  const factoryForKey = (key) => {
    if (key === "environment") return emptyEnvRow();
    if (key === "schedule") return emptyScheduleRow();
    if (key === "features") return emptyFeatureRow();
    if (key === "team") return emptyTeamRow();
    return "";
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Test Plan Name is required.";
    return next;
  };

  const loadUsers = useCallback(async () => {
    if (!resolvedClientId) return;
    setUsersLoading(true);
    try {
      const res = await userService.findUsers(
        {
          client_id: resolvedClientId,
          is_active: true,
          is_deleted: false,
        },
        1,
        200,
      );
      const list = res?.data?.users ?? res?.data?.data?.users ?? [];
      setUsers(Array.isArray(list) ? list : []);
    } catch {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [resolvedClientId]);

  const loadProgress = useCallback(async () => {
    if (!editId || !projectId) return;
    try {
      const res = await testPlanService.getProgress(editId, projectId);
      const d = res?.data ?? res;
      setProgress({
        totalRuns: d?.totalRuns ?? 0,
        completedRuns: d?.completedRuns ?? 0,
        inProgress: d?.inProgress ?? 0,
      });
    } catch {
      setProgress(null);
    }
  }, [editId, projectId]);

  const applyPlanData = useCallback((p) => {
    if (!p) return;
    setForm(mapPlanToForm(p));
    setLinkedRunIds(Array.isArray(p.linkedRunIds) ? p.linkedRunIds : []);
  }, []);

  const loadPlan = useCallback(async () => {
    if (!editId || !projectId) return;
    setLoading(true);
    try {
      const res = await testPlanService.get(editId, projectId);
      const p = res?.data ?? res;
      applyPlanData(p);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to load test plan",
      );
    } finally {
      setLoading(false);
    }
  }, [editId, projectId, applyPlanData]);

  const loadClone = useCallback(async () => {
    if (editId || !cloneId || !projectId) return;
    setLoading(true);
    try {
      const res = await testPlanService.get(cloneId, projectId);
      const p = res?.data ?? res;
      if (p) {
        const mapped = mapPlanToForm(p);
        setForm({
          ...mapped,
          name: p.name ? `${p.name} (copy)` : "",
        });
        setLinkedRunIds([]);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to load plan to clone",
      );
    } finally {
      setLoading(false);
    }
  }, [cloneId, editId, projectId]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  useEffect(() => {
    loadClone();
  }, [loadClone]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const totalCases = 0;
  const overallProgress = useMemo(() => {
    if (!progress || !progress.totalRuns) return 0;
    return Math.round((progress.completedRuns / progress.totalRuns) * 100);
  }, [progress]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    const payload = buildPayload(form, user?.user_id);

    setSaving(true);
    try {
      const createOrUpdatePayload = {
        ...payload,
        ...(resolvedClientId ? { client_id: resolvedClientId } : {}),
      };
      if (editId) {
        await testPlanService.update(editId, projectId, createOrUpdatePayload);
        toast.success("Test plan updated");
      } else {
        await testPlanService.create({
          project_id: projectId,
          ...createOrUpdatePayload,
        });
        toast.success("Test plan created");
      }
      const backQs = new URLSearchParams();
      backQs.set("project_id", projectId);
      backQs.set("project_name", projectName);
      if (resolvedClientId) backQs.set("client_id", resolvedClientId);
      navigate(`/test-plans?${backQs.toString()}`);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to save test plan";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const backQs = new URLSearchParams();
    backQs.set("project_id", projectId);
    backQs.set("project_name", projectName);
    if (resolvedClientId) backQs.set("client_id", resolvedClientId);
    navigate(`/test-plans?${backQs.toString()}`);
  };

  const newTestRunHref = editId
    ? `/test-runs/new?project_id=${encodeURIComponent(projectId)}&test_plan_id=${encodeURIComponent(editId)}`
    : `/test-runs/new?project_id=${encodeURIComponent(projectId)}`;

  if (!projectId) {
    return (
      <div className="p-6">
        <p className="font-semibold text-rose-600">Project ID is required.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="min-h-[40vh] p-6 max-w-3xl mx-auto"
        data-testid="create-test-plan-loading"
      >
        <FormSkeleton fields={8} className="max-w-none mx-0" />
      </div>
    );
  }

  const sectionTitle = (t) => (
    <h3 className="text-sm font-semibold text-text-primary">{t}</h3>
  );

  return (
    <div
      className="min-h-screen bg-gray-50/80 p-6"
      data-testid="create-test-plan-page"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-violet-600"
            onClick={handleCancel}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>
              Test Plan /{" "}
              <span className="font-semibold text-text-primary">{title}</span>
            </span>
          </button>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="border-rose-300 text-rose-600 hover:bg-rose-50"
              data-testid="cancel-create-test-plan"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="create-test-plan-form"
              className="bg-violet-700 hover:bg-violet-800"
              data-testid="submit-create-test-plan"
              disabled={saving}
            >
              {saving ? "Saving…" : editId ? "Save" : "Create"}
            </Button>
          </div>
        </div>

        <form id="create-test-plan-form" onSubmit={handleSubmit}>
          <Card className="rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-10 lg:gap-8">
              <div className="space-y-5 lg:col-span-7">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1 space-y-2">
                    <Label htmlFor="tp-name" className="text-text-primary">
                      Test Plan Name <span className="text-rose-600">*</span>
                    </Label>
                    <Input
                      id="tp-name"
                      className="rounded-lg border-gray-200"
                      placeholder="Give your Test Plan a title..."
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      aria-invalid={Boolean(errors.name)}
                      data-testid="input-test-plan-name"
                    />
                    {errors.name && submitAttempted && (
                      <p className="text-sm text-rose-600" role="alert">
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div className="w-full shrink-0 sm:max-w-[220px]">
                    <div className="rounded-lg border border-gray-100 bg-gray-50/90 p-3 text-sm">
                      <div className="flex justify-between gap-4 text-text-secondary">
                        <span>Total Test Case(s):</span>
                        <span className="font-semibold text-text-primary">
                          {totalCases}
                        </span>
                      </div>
                      <div className="mt-0.5 flex justify-between gap-4 text-text-secondary">
                        <span>Overall Progress:</span>
                        <span className="font-semibold text-text-primary">
                          {overallProgress}%
                        </span>
                      </div>
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                          className="h-full rounded-full bg-violet-700 transition-all"
                          style={{ width: `${overallProgress}%` }}
                        />
                      </div>
                      {editId && progress && (
                        <p className="mt-2 text-xs text-text-secondary">
                          Runs: {progress.totalRuns} linked ·{" "}
                          {progress.completedRuns} completed ·{" "}
                          {progress.inProgress} active
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tp-assigned">Assigned to</Label>
                  <Select
                    value={form.assignedTo || "__default"}
                    onValueChange={(v) =>
                      update("assignedTo", v === "__default" ? "" : v)
                    }
                    disabled={usersLoading}
                  >
                    <SelectTrigger
                      id="tp-assigned"
                      className="rounded-lg border-gray-200"
                    >
                      <SelectValue
                        placeholder={usersLoading ? "—" : "Select owner"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default">
                        Use current user (default)
                      </SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.user_id} value={String(u.user_id)}>
                          {u.full_name || u.email || u.user_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tp-ref">Reference</Label>
                  <Input
                    id="tp-ref"
                    className="rounded-lg border-gray-200"
                    placeholder="Add reference ID's to external tickets here."
                    value={form.reference}
                    onChange={(e) => update("reference", e.target.value)}
                    data-testid="input-reference"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="tp-start">Start Date</Label>
                    <Input
                      id="tp-start"
                      type="date"
                      className="rounded-lg border-gray-200"
                      value={form.startDate}
                      onChange={(e) => update("startDate", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tp-end">End Date</Label>
                    <Input
                      id="tp-end"
                      type="date"
                      className="rounded-lg border-gray-200"
                      value={form.endDate}
                      onChange={(e) => update("endDate", e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tp-notify">Notifications</Label>
                  <Input
                    id="tp-notify"
                    className="rounded-lg border-gray-200"
                    placeholder="Enter email separated by commas (informational; not stored separately yet)."
                    value={form.notifications}
                    onChange={(e) => update("notifications", e.target.value)}
                    data-testid="input-notifications"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Test runs</Label>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Button
                      type="button"
                      variant="outline"
                      className="shrink-0 border-violet-300 text-violet-800 hover:bg-violet-50"
                      disabled={!editId}
                      onClick={() => editId && setLinkModalOpen(true)}
                      data-testid="btn-link-test-run"
                    >
                      Link existing test run
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="shrink-0 border-blue-400 text-blue-700 hover:bg-blue-50"
                      data-testid="btn-create-new-test-run"
                      onClick={() => navigate(newTestRunHref)}
                    >
                      Create New Test Run
                    </Button>
                  </div>
                  {editId && linkedRunIds.length > 0 && (
                    <p className="text-xs text-text-secondary">
                      {linkedRunIds.length} test run(s) linked to this plan.
                    </p>
                  )}
                  {!editId && (
                    <p className="text-xs text-text-secondary">
                      Save the plan first, then open it again to link test runs.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex min-h-[420px] flex-col lg:col-span-3">
                <Label htmlFor="tp-desc" className="mb-2">
                  Description
                </Label>
                <textarea
                  id="tp-desc"
                  className="min-h-[360px] flex-1 resize-none rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-text-primary shadow-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                  placeholder="Use this description to describe the purpose of this test plan."
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  data-testid="textarea-description"
                />
              </div>
            </div>

            <div className="mt-8 space-y-8 border-t border-gray-100 pt-8">
              <div className="space-y-3">
                {sectionTitle("Scope — In")}
                <p className="text-xs text-text-secondary">
                  What is included in this test plan.
                </p>
                {form.scopeIn.map((row, i) => (
                  <div key={`si-${i}`} className="flex gap-2">
                    <Input
                      className="rounded-lg border-gray-200"
                      value={row}
                      onChange={(e) =>
                        updateArrayItem("scopeIn", i, null, e.target.value)
                      }
                      placeholder="Scope item"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => addArrayRow("scopeIn", () => "")}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => removeArrayRow("scopeIn", i)}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                {sectionTitle("Scope — Out")}
                <p className="text-xs text-text-secondary">
                  Explicitly excluded from this plan.
                </p>
                {form.scopeOut.map((row, i) => (
                  <div key={`so-${i}`} className="flex gap-2">
                    <Input
                      className="rounded-lg border-gray-200"
                      value={row}
                      onChange={(e) =>
                        updateArrayItem("scopeOut", i, null, e.target.value)
                      }
                      placeholder="Out of scope item"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => addArrayRow("scopeOut", () => "")}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => removeArrayRow("scopeOut", i)}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                {sectionTitle("Test strategy")}
                <div className="space-y-2">
                  <Label className="text-xs text-text-secondary">
                    Testing types
                  </Label>
                  {form.testingTypes.map((row, i) => (
                    <div key={`tt-${i}`} className="flex gap-2">
                      <Input
                        className="rounded-lg border-gray-200"
                        value={row}
                        onChange={(e) =>
                          updateArrayItem(
                            "testingTypes",
                            i,
                            null,
                            e.target.value,
                          )
                        }
                        placeholder="e.g. Functional, Regression"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => addArrayRow("testingTypes", () => "")}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeArrayRow("testingTypes", i)}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-text-secondary">
                    Execution approach
                  </Label>
                  {form.executionApproach.map((row, i) => (
                    <div key={`ea-${i}`} className="flex gap-2">
                      <Input
                        className="rounded-lg border-gray-200"
                        value={row}
                        onChange={(e) =>
                          updateArrayItem(
                            "executionApproach",
                            i,
                            null,
                            e.target.value,
                          )
                        }
                        placeholder="How tests will be executed"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() =>
                          addArrayRow("executionApproach", () => "")
                        }
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeArrayRow("executionApproach", i)}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-text-secondary">Tools</Label>
                  {form.tools.map((row, i) => (
                    <div key={`tl-${i}`} className="flex gap-2">
                      <Input
                        className="rounded-lg border-gray-200"
                        value={row}
                        onChange={(e) =>
                          updateArrayItem("tools", i, null, e.target.value)
                        }
                        placeholder="Tool name"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => addArrayRow("tools", () => "")}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeArrayRow("tools", i)}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {sectionTitle("Environment")}
                {form.environment.map((row, i) => (
                  <div
                    key={`env-${i}`}
                    className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-center"
                  >
                    <Input
                      placeholder="Environment"
                      className="rounded-lg border-gray-200"
                      value={row.environment}
                      onChange={(e) =>
                        updateArrayItem(
                          "environment",
                          i,
                          "environment",
                          e.target.value,
                        )
                      }
                    />
                    <Input
                      placeholder="Purpose"
                      className="rounded-lg border-gray-200"
                      value={row.purpose}
                      onChange={(e) =>
                        updateArrayItem(
                          "environment",
                          i,
                          "purpose",
                          e.target.value,
                        )
                      }
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => addArrayRow("environment", emptyEnvRow)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeArrayRow("environment", i)}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                {sectionTitle("Schedule")}
                {form.schedule.map((row, i) => (
                  <div
                    key={`sch-${i}`}
                    className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto_auto] sm:items-center"
                  >
                    <Input
                      placeholder="Activity / milestone"
                      className="rounded-lg border-gray-200"
                      value={row.activity}
                      onChange={(e) =>
                        updateArrayItem(
                          "schedule",
                          i,
                          "activity",
                          e.target.value,
                        )
                      }
                    />
                    <Input
                      type="date"
                      className="rounded-lg border-gray-200"
                      value={row.date}
                      onChange={(e) =>
                        updateArrayItem("schedule", i, "date", e.target.value)
                      }
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => addArrayRow("schedule", emptyScheduleRow)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeArrayRow("schedule", i)}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                {sectionTitle("Features in scope")}
                <div className="overflow-x-auto rounded-lg border border-gray-100">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50/80 text-left text-xs font-medium text-text-secondary">
                        <th className="px-3 py-2">Reference</th>
                        <th className="px-3 py-2">Feature</th>
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2">QA owner</th>
                        <th className="px-3 py-2">Start</th>
                        <th className="px-3 py-2">End</th>
                        <th className="w-20 px-2 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {form.features.map((row, i) => (
                        <tr
                          key={`feat-${i}`}
                          className="border-b border-gray-50"
                        >
                          <td className="px-2 py-1">
                            <Input
                              className="h-9 rounded border-gray-200"
                              value={row.reference}
                              onChange={(e) =>
                                updateArrayItem(
                                  "features",
                                  i,
                                  "reference",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              className="h-9 rounded border-gray-200"
                              value={row.feature_title}
                              onChange={(e) =>
                                updateArrayItem(
                                  "features",
                                  i,
                                  "feature_title",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              className="h-9 rounded border-gray-200"
                              value={row.type}
                              onChange={(e) =>
                                updateArrayItem(
                                  "features",
                                  i,
                                  "type",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              className="h-9 rounded border-gray-200"
                              value={row.qa_owner}
                              onChange={(e) =>
                                updateArrayItem(
                                  "features",
                                  i,
                                  "qa_owner",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="date"
                              className="h-9 rounded border-gray-200"
                              value={row.start_date}
                              onChange={(e) =>
                                updateArrayItem(
                                  "features",
                                  i,
                                  "start_date",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="date"
                              className="h-9 rounded border-gray-200"
                              value={row.end_date}
                              onChange={(e) =>
                                updateArrayItem(
                                  "features",
                                  i,
                                  "end_date",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td className="px-1 py-1">
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() =>
                                  addArrayRow("features", emptyFeatureRow)
                                }
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => removeArrayRow("features", i)}
                              >
                                <Trash2 className="h-4 w-4 text-rose-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                <div className="space-y-3">
                  {sectionTitle("Entry criteria")}
                  {form.entryCriteria.map((row, i) => (
                    <div key={`ec-${i}`} className="flex gap-2">
                      <Input
                        className="rounded-lg border-gray-200"
                        value={row}
                        onChange={(e) =>
                          updateArrayItem(
                            "entryCriteria",
                            i,
                            null,
                            e.target.value,
                          )
                        }
                        placeholder="Criterion"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => addArrayRow("entryCriteria", () => "")}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeArrayRow("entryCriteria", i)}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="space-y-3">
                  {sectionTitle("Exit criteria")}
                  {form.exitCriteria.map((row, i) => (
                    <div key={`xc-${i}`} className="flex gap-2">
                      <Input
                        className="rounded-lg border-gray-200"
                        value={row}
                        onChange={(e) =>
                          updateArrayItem(
                            "exitCriteria",
                            i,
                            null,
                            e.target.value,
                          )
                        }
                        placeholder="Criterion"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => addArrayRow("exitCriteria", () => "")}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeArrayRow("exitCriteria", i)}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {sectionTitle("Team")}
                {form.team.map((row, i) => (
                  <div
                    key={`tm-${i}`}
                    className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1.2fr)_1fr_1fr_auto_auto] sm:items-center"
                  >
                    <Select
                      value={row.user_id || "__none"}
                      onValueChange={(v) =>
                        updateArrayItem(
                          "team",
                          i,
                          "user_id",
                          v === "__none" ? "" : v,
                        )
                      }
                    >
                      <SelectTrigger className="rounded-lg border-gray-200">
                        <SelectValue placeholder="Member" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">—</SelectItem>
                        {users.map((u) => (
                          <SelectItem key={u.user_id} value={String(u.user_id)}>
                            {u.full_name || u.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Role"
                      className="rounded-lg border-gray-200"
                      value={row.role}
                      onChange={(e) =>
                        updateArrayItem("team", i, "role", e.target.value)
                      }
                    />
                    <Input
                      placeholder="Responsibility"
                      className="rounded-lg border-gray-200"
                      value={row.responsibility}
                      onChange={(e) =>
                        updateArrayItem(
                          "team",
                          i,
                          "responsibility",
                          e.target.value,
                        )
                      }
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => addArrayRow("team", emptyTeamRow)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeArrayRow("team", i)}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </form>
      </div>

      {editId && (
        <LinkTestRunModal
          open={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          projectId={projectId}
          planId={editId}
          linkedRunIds={linkedRunIds}
          onLinked={() => {
            loadPlan();
            loadProgress();
          }}
        />
      )}
    </div>
  );
};

export default CreateTestPlan;
